import React, { useState, useEffect } from "react";
import { Grid2, Typography, Paper,  } from "@mui/material";
import { useForm } from 'react-hook-form';
import axios from "axios";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from 'zod';

import BreadCrumbs from "@components/breadCrumbs/breadCrumbs";
import CustomButton from "@components/button/customButton";
import ConfirmDialog from "@components/dialog/confirmDialog";
import ControlledTextField from "@components/inputs/controlledTextField";
import SkeletonWrapper from "@components/skeleton/skeletonWrapper";

import ModuleSelection from "./moduleSelection";
import AuthorizedUserListing from "./authorizedUserListing";
import AddUserDialog from "./addUserDialog";

import { useSnackbar } from "@context/snackbarContext";
import { useAuthentication } from "@redux/authentication/hook";

import { AdminApiEndpoints } from "@configuration/apiEndpoints";

const schema = z.object({
  roleName: z.string().min(1, 'Role Name is required'),
  description: z.string().min(1, 'Description is required'),
})

export default function RoleForm() {
  const queryClient = useQueryClient()
  const openSnackbar = useSnackbar();
  const navigate = useNavigate()
  const { pathname } = useLocation();
  const { user } = useAuthentication()
  const pageAction = pathname.split("/")[3] || null; // get from pathname expected values ["create-role" || "view-role" || "edit-role"]
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  const [initialized, setInitialized] = useState(false);

  const { handleSubmit, control, getValues, reset } = useForm({
    resolver: zodResolver(schema),
  });

  const [openAddUserDialog, setOpenAddUserDialog] = useState(false)
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)

  const [addedUserListing, setAddedUserListing] = useState([])

  const [module, setModule] = useState([])

  const [addedModule, setAddedModule] = useState([])

  useEffect(()=>{
    if (((pageAction === "edit-role" || pageAction === "view-role") && !id) || (pageAction === "create-role" && id)) {
      onBack()
    }
    fetchModuleMutation.mutate()
    if (id) {
      fetchRoleMutation.mutate(id)
    }
  },[pageAction])

  const userOptions = useQuery({
    queryKey: ['getUserList'],
    queryFn: () =>
      axios.get(AdminApiEndpoints.getUserList),
    initialData: [],
    select: (res) => {
      return res?.data || []
    },
  })

  const fetchModuleMutation = useMutation({
    mutationFn: () => {
      return axios.get(`${AdminApiEndpoints.getModule}`)
    },
    onSuccess: (res) => {
      const data = res.data
      const mapped = data?.map((x) => {
        return {
          moduleGuid: x.moduleGuid,
          moduleName: x.moduleName,
          isAdminOnly: x.isAdmin,
          grandAccess: false,
          isAdmin: false,
        };
      })
      setModule(mapped)
    },
    onError: (err) => {
      openSnackbar("Failed to retrieve module", "error");
    }
  });

  const fetchRoleMutation = useMutation({
    mutationFn: (id) => {
      return axios.get(`${AdminApiEndpoints.getRole}/${id}`)
    },
    onSuccess: (res) => {
      const data = res.data
      reset(data)
      setAddedModule(data.roleModules)
      const users = data?.userRoles.map((x) => {
        return {
          userGuid: x.user.userGuid,
          fullName: x.user.fullName,
          createdBy: x.createdBy,
          status: x.user.status,
        };
      })
      setAddedUserListing(users)
    },
    onError: (err) => {
      openSnackbar("Failed to retrieve role", "error");
    }
  });

  useEffect(() => {
    if (!initialized && module.length > 0 && addedModule.length > 0) {
      const updatedDataA = module?.map(itemA => {
        const match = addedModule?.find(itemB => itemB.moduleGuid === itemA.moduleGuid);
        return match ? { ...itemA, grandAccess: match.grandAccess , isAdmin: match.isAdmin } : itemA;
      });

      // Only update state if there are actual changes
      if (JSON.stringify(module) !== JSON.stringify(updatedDataA)) {
        setModule(updatedDataA);
      }

      setInitialized(true);
    }
  }, [initialized, addedModule, module]);

  const createRoleMutation = useMutation({
    mutationFn: (data) => {
      return axios.post(AdminApiEndpoints.createRole, {...data, by: user?.username})
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getRole'] })
      openSnackbar("Successfully created role", "success");
      onBack()
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      if (err?.response) openSnackbar(err?.response?.data, "error");
      else openSnackbar("Failed to create role", "error");
    }
  })

  const updateRoleMutation = useMutation({
    mutationFn: (data) => {
      return axios.put(`${AdminApiEndpoints.updateRole}/${id}`, {...data, by: user?.username})
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getRole'] })
      openSnackbar("Successfully updated role", "success");
      fetchRoleMutation.mutate(id)
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      if (err?.response) openSnackbar(err?.response?.data, "error");
      else openSnackbar("Failed to update role", "error");
    }
  })

  const onSubmit = () => {
    setOpenConfirmDialog(true)
  };

  const onConfirmSubmit = () => {
    const data = getValues();
    data.roleModule = module?.filter((x) => x.grandAccess)
    ?.map((x) => {
      return {
        moduleGuid: x.moduleGuid,
        isAdmin: x.isAdmin ? true : false,
        grandAccess: true,
      };
    })
    data.userRoles = addedUserListing?.map((x) => {
      return {
        userGuid: x.userGuid,
      };
    })
    // console.log(data)
    // return
    if(pageAction === "create-role")
      createRoleMutation.mutate(data)
    else
      updateRoleMutation.mutate(data)
  }

  const onBack = () => {
    navigate('/admin/security?tab=roles', { replace: true });
  }

  // Filter out user already in addedUserListing from userListing when rendering
  const filteredAddUserListing = userOptions.data?.filter(
    (user) =>
      !addedUserListing.some((addedUser) => addedUser.userGuid === user.userGuid)
  );

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: "25px"}}>
      <BreadCrumbs />
      <SkeletonWrapper isLoading={fetchRoleMutation.isPending} isError={fetchRoleMutation.isError} errorMessage="Unable to load role" disabled={["create-role"].includes(pageAction)}>
      <form onSubmit={handleSubmit(onSubmit)} style={{display: 'flex', flexDirection: 'column', gap: "25px"}}>
      <Grid2 container columnSpacing={'20px'} rowSpacing={'25px'}>
        <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }} style={{ display: 'flex', flexDirection: 'column' }}>
          <Paper
            sx={{
              p: "20px 25px",
              boxShadow: "none",
              borderRadius: "10px",
              border: "1px solid #E5E5E5",
              flexGrow: 1,
            }}
          >
            <Grid2 container spacing={"20px"}>
              <Grid2 size={12}>
                <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>Role Details</Typography>
              </Grid2>
              <Grid2 size={12}>
                <ControlledTextField 
                  name="roleName"
                  control={control}
                  label="Role Name"
                  disabled={fetchRoleMutation.isPending || fetchRoleMutation.isError}
                  readOnly={["view-role"].includes(pageAction)}
                />
              </Grid2>
              <Grid2 size={12}>
                <ControlledTextField 
                  name="description"
                  control={control}
                  label="Description"
                  disabled={fetchRoleMutation.isPending || fetchRoleMutation.isError}
                  readOnly={["view-role"].includes(pageAction)}
                  multiline
                  rows={3}
                />
              </Grid2>
            </Grid2>
          </Paper>
        </Grid2>

        <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
          <ModuleSelection
            module={module}
            setModule={setModule}
            loading={false}
            disabled={["view-role"].includes(pageAction)}
          />
        </Grid2>

        <Grid2 size={12}>
          <AuthorizedUserListing 
            addedUserListing={addedUserListing}
            loading={userOptions.isFetching}
            pageAction={pageAction}
            handleOpenAddUserDialog={()=>setOpenAddUserDialog(true)}
            setAddedUserListing={setAddedUserListing}
            disabled={["view-role"].includes(pageAction)}
          />
        </Grid2>
      </Grid2>

      {["create-role", "edit-role"].includes(pageAction) && 
        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
            display: "flex",
            justifyContent: 'flex-end',
            gap: "10px"
          }}
        >
          <CustomButton type="submit" disabled={fetchRoleMutation.isPending || fetchRoleMutation.isError}>Submit</CustomButton>
        </Paper>
      }
      </form>
      </SkeletonWrapper>

      <AddUserDialog 
        open={openAddUserDialog}
        onClose={()=>setOpenAddUserDialog(false)}
        userListing={filteredAddUserListing}
        setAddedUserListing={setAddedUserListing}
      />

      <ConfirmDialog
        open={openConfirmDialog}
        onClose={()=>setOpenConfirmDialog(false)}
        dialogTitle="Confirmation"
        dialogDescription="Submit role?"
        onSubmit={()=>onConfirmSubmit()}
        loading={createRoleMutation.isPending || updateRoleMutation.isPending}
      />
    </div>
  );
}